<template>
  <div class="form-field-renderer">
    <!-- Bereich/Section -->
    <div v-if="field.type === 'section'" class="formpublisher__section">
      <h3 class="formpublisher__section-title">
        <a
          v-if="hasFieldLink"
          :href="normalizedFieldLink"
          target="_blank"
          rel="noopener noreferrer"
          class="form-field-renderer__label-link"
        >
          {{ field.label }}
        </a>
        <template v-else>
          {{ field.label }}
        </template>
      </h3>
      <p v-if="field.helpText" class="formpublisher__section-description">
        {{ field.helpText }}
      </p>
    </div>

    <!-- Textfelder -->
    <v-text-field
      v-else-if="field.type === 'text'"
      :value="value"
      :label="field.label"
      :placeholder="field.placeholder"
      :hint="field.helpText"
      :required="field.required"
      :rules="getValidationRules(field)"
      outlined
      dense
      @input="$emit('input', $event)"
    >
      <template v-slot:label>
        <a
          v-if="hasFieldLink"
          :href="normalizedFieldLink"
          target="_blank"
          rel="noopener noreferrer"
          class="form-field-renderer__label-link"
          @click.stop
        >
          {{ field.label }}
        </a>
        <span v-else>{{ field.label }}</span>
      </template>
    </v-text-field>

    <!-- E-Mail -->
    <v-text-field
      v-else-if="field.type === 'email'"
      :value="value"
      :label="field.label"
      :placeholder="field.placeholder"
      :hint="field.helpText"
      type="email"
      :required="field.required"
      :rules="getValidationRules(field)"
      outlined
      dense
      @input="$emit('input', $event)"
    >
      <template v-slot:label>
        <a
          v-if="hasFieldLink"
          :href="normalizedFieldLink"
          target="_blank"
          rel="noopener noreferrer"
          class="form-field-renderer__label-link"
          @click.stop
        >
          {{ field.label }}
        </a>
        <span v-else>{{ field.label }}</span>
      </template>
    </v-text-field>

    <!-- Telefon -->
    <v-text-field
      v-else-if="field.type === 'phone'"
      :value="value"
      :label="field.label"
      :placeholder="field.placeholder"
      :hint="field.helpText"
      type="tel"
      :required="field.required"
      :rules="getValidationRules(field)"
      outlined
      dense
      @input="$emit('input', $event)"
    >
      <template v-slot:label>
        <a
          v-if="hasFieldLink"
          :href="normalizedFieldLink"
          target="_blank"
          rel="noopener noreferrer"
          class="form-field-renderer__label-link"
          @click.stop
        >
          {{ field.label }}
        </a>
        <span v-else>{{ field.label }}</span>
      </template>
    </v-text-field>

    <!-- URL -->
    <v-text-field
      v-else-if="field.type === 'url'"
      :value="value"
      :label="field.label"
      :placeholder="field.placeholder"
      :hint="field.helpText"
      type="url"
      :required="field.required"
      :rules="getValidationRules(field)"
      outlined
      dense
      @input="$emit('input', $event)"
    >
      <template v-slot:label>
        <a
          v-if="hasFieldLink"
          :href="normalizedFieldLink"
          target="_blank"
          rel="noopener noreferrer"
          class="form-field-renderer__label-link"
          @click.stop
        >
          {{ field.label }}
        </a>
        <span v-else>{{ field.label }}</span>
      </template>
    </v-text-field>

    <!-- Passwort -->
    <v-text-field
      v-else-if="field.type === 'password'"
      :value="value"
      :label="field.label"
      :placeholder="field.placeholder"
      :hint="field.helpText"
      type="password"
      :required="field.required"
      :rules="getValidationRules(field)"
      outlined
      dense
      @input="$emit('input', $event)"
    >
      <template v-slot:label>
        <a
          v-if="hasFieldLink"
          :href="normalizedFieldLink"
          target="_blank"
          rel="noopener noreferrer"
          class="form-field-renderer__label-link"
          @click.stop
        >
          {{ field.label }}
        </a>
        <span v-else>{{ field.label }}</span>
      </template>
    </v-text-field>

    <!-- Zahlenfelder -->
    <v-text-field
      v-else-if="field.type === 'number'"
      :value="value"
      :label="field.label"
      :placeholder="field.placeholder"
      :hint="field.helpText"
      type="number"
      :required="field.required"
      :rules="getValidationRules(field)"
      outlined
      :step="field.step ?? 1"
      dense
      @input="$emit('input', $event)"
    >
      <template v-slot:label>
        <a
          v-if="hasFieldLink"
          :href="normalizedFieldLink"
          target="_blank"
          rel="noopener noreferrer"
          class="form-field-renderer__label-link"
          @click.stop
        >
          {{ field.label }}
        </a>
        <span v-else>{{ field.label }}</span>
      </template>
    </v-text-field>

    <!-- Textbereich -->
    <v-textarea
      v-else-if="field.type === 'textarea'"
      :value="value"
      :label="field.label"
      :placeholder="field.placeholder"
      :hint="field.helpText"
      :required="field.required"
      :rules="getValidationRules(field)"
      outlined
      rows="4"
      @input="$emit('input', $event)"
    >
      <template v-slot:label>
        <a
          v-if="hasFieldLink"
          :href="normalizedFieldLink"
          target="_blank"
          rel="noopener noreferrer"
          class="form-field-renderer__label-link"
          @click.stop
        >
          {{ field.label }}
        </a>
        <span v-else>{{ field.label }}</span>
      </template>
    </v-textarea>

    <!-- Dropdown -->
    <v-select
      v-else-if="field.type === 'select'"
      :value="value"
      :label="field.label"
      :hint="field.helpText"
      :items="field.options"
      item-text="label"
      item-value="value"
      :required="field.required"
      :rules="getValidationRules(field)"
      outlined
      dense
      @input="$emit('input', $event)"
    >
      <template v-slot:label>
        <a
          v-if="hasFieldLink"
          :href="normalizedFieldLink"
          target="_blank"
          rel="noopener noreferrer"
          class="form-field-renderer__label-link"
          @click.stop
        >
          {{ field.label }}
        </a>
        <span v-else>{{ field.label }}</span>
      </template>
    </v-select>

    <!-- Radio Buttons -->
    <div v-else-if="field.type === 'radio'">
      <label class="d-block mb-2">
        <a
          v-if="hasFieldLink"
          :href="normalizedFieldLink"
          target="_blank"
          rel="noopener noreferrer"
          class="form-field-renderer__label-link"
        >
          {{ field.label }}
        </a>
        <template v-else>
          {{ field.label }}
        </template>
        <span v-if="field.required" class="red--text">*</span>
      </label>
      <v-radio-group
        v-model="radioModel"
        :name="field.id"
        :rules="getValidationRules(field)"
      >
        <v-radio
          v-for="option in optionItems"
          :key="option.key"
          :label="option.label"
          :value="option.token"
        ></v-radio>
      </v-radio-group>
      <small v-if="field.helpText" class="grey--text">{{
        field.helpText
      }}</small>
    </div>

    <!-- Checkbox -->
    <div v-else-if="field.type === 'checkbox'">
      <label class="d-block mb-2">
        <a
          v-if="hasFieldLink"
          :href="normalizedFieldLink"
          target="_blank"
          rel="noopener noreferrer"
          class="form-field-renderer__label-link"
        >
          {{ field.label }}
        </a>
        <template v-else>
          {{ field.label }}
        </template>
        <span v-if="field.required" class="red--text">*</span>
      </label>
      <div>
        <v-checkbox
          v-for="option in optionItems"
          :key="option.key"
          :input-value="isOptionSelected(option.token)"
          :label="option.label"
          @change="onCheckboxChange(option.token, $event)"
        ></v-checkbox>
      </div>
      <small v-if="field.helpText" class="grey--text">{{
        field.helpText
      }}</small>
    </div>

    <!-- Checkbox mit Bildern -->
    <div v-else-if="field.type === 'checkbox-image'">
      <label class="d-block mb-2">
        <a
          v-if="hasFieldLink"
          :href="normalizedFieldLink"
          target="_blank"
          rel="noopener noreferrer"
          class="form-field-renderer__label-link"
        >
          {{ field.label }}
        </a>
        <template v-else>
          {{ field.label }}
        </template>
        <span v-if="field.required" class="red--text">*</span>
      </label>
      <div class="form-field-renderer__image-options">
        <div
          v-for="option in optionItems"
          :key="option.key"
          class="form-field-renderer__image-option"
          :class="{
            selected: isOptionSelected(option.token),
            'is-disabled':
              !isOptionSelected(option.token) && !canSelectMoreCheckboxOptions
          }"
          @click="toggleCheckboxValue(option.token)"
        >
          <v-img
            v-if="getOptionImageUrl(option)"
            :src="getOptionImageUrl(option)"
            height="110"
            contain
            class="mb-2"
          ></v-img>
          <div class="text-body-2">{{ option.label }}</div>
        </div>
      </div>
      <small v-if="maxSelections" class="grey--text d-block mt-1">
        Maximal {{ maxSelections }} Auswahl{{ maxSelections > 1 ? "en" : "" }}
      </small>
      <small v-if="field.helpText" class="grey--text">{{
        field.helpText
      }}</small>
    </div>

    <!-- Datumfield -->
    <v-text-field
      v-else-if="field.type === 'date'"
      :value="value"
      :label="field.label"
      :hint="field.helpText"
      type="date"
      :required="field.required"
      :rules="getValidationRules(field)"
      outlined
      dense
      @input="$emit('input', $event)"
    >
      <template v-slot:label>
        <a
          v-if="hasFieldLink"
          :href="normalizedFieldLink"
          target="_blank"
          rel="noopener noreferrer"
          class="form-field-renderer__label-link"
          @click.stop
        >
          {{ field.label }}
        </a>
        <span v-else>{{ field.label }}</span>
      </template>
    </v-text-field>

    <!-- Dateiupload -->
    <v-file-input
      v-else-if="field.type === 'file'"
      :value="value"
      :label="field.label"
      :hint="field.helpText"
      :required="field.required"
      :rules="getValidationRules(field)"
      :accept="getAcceptByFieldType(field.type)"
      outlined
      dense
      @change="$emit('input', $event)"
    >
      <template v-slot:label>
        <a
          v-if="hasFieldLink"
          :href="normalizedFieldLink"
          target="_blank"
          rel="noopener noreferrer"
          class="form-field-renderer__label-link"
          @click.stop
        >
          {{ field.label }}
        </a>
        <span v-else>{{ field.label }}</span>
      </template>
    </v-file-input>

    <!-- Bild-Upload -->
    <v-file-input
      v-else-if="field.type === 'image'"
      :value="value"
      :label="field.label"
      :hint="field.helpText"
      :required="field.required"
      :rules="getValidationRules(field)"
      :accept="getAcceptByFieldType(field.type)"
      outlined
      dense
      @change="$emit('input', $event)"
    >
      <template v-slot:label>
        <a
          v-if="hasFieldLink"
          :href="normalizedFieldLink"
          target="_blank"
          rel="noopener noreferrer"
          class="form-field-renderer__label-link"
          @click.stop
        >
          {{ field.label }}
        </a>
        <span v-else>{{ field.label }}</span>
      </template>
    </v-file-input>

    <!-- Verborgenes Feld -->
    <input
      v-else-if="field.type === 'hidden'"
      :value="value"
      type="hidden"
      @input="$emit('input', $event.target.value)"
    />

    <!-- HTML Content -->
    <div
      v-else-if="field.type === 'html'"
      class="formpublisher__html-content mb-4"
    >
      <div v-html="field.label"></div>
    </div>

    <!-- Link -->
    <div v-else-if="field.type === 'link'" class="formpublisher__link mb-4">
      <a
        :href="field.placeholder"
        target="_blank"
        rel="noopener noreferrer"
        class="primary--text"
      >
        {{ field.label || field.placeholder }}
        <v-icon small>mdi-open-in-new</v-icon>
      </a>
    </div>

    <!-- Submit-Button -->
    <div v-else-if="field.type === 'submit'" class="mt-6 mb-4">
      <v-btn class="custom primary" large type="submit" block>
        {{ field.label || "Senden" }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import {
  getAllowedMimeTypesByFieldType,
  isAllowedMimeType
} from "./fileMimeTypes";

export default {
  name: "FormFieldRenderer",
  data() {
    return {
      localCheckboxValues: [],
      localRadioValue: ""
    };
  },
  props: {
    field: {
      type: Object,
      required: true
    },
    value: {
      type: [String, Number, Array],
      default: null
    }
  },
  watch: {
    value: {
      handler(newValue) {
        this.syncFromProp(newValue);
      },
      immediate: true
    },
    "field.type": {
      handler() {
        this.syncFromProp(this.value);
      },
      immediate: true
    }
  },
  computed: {
    optionItems() {
      return (this.field?.options || []).map((option, index) => {
        const value = this.getOptionValue(option);
        const token = `idx:${index}`;

        return {
          key: option.id || `${token}-${index}`,
          label: option.label,
          value,
          token,
          image: option.image
        };
      });
    },
    radioModel: {
      get() {
        return this.localRadioValue;
      },
      set(newValue) {
        this.localRadioValue = String(newValue || "");
        this.$emit("input", this.localRadioValue);
      }
    },
    maxSelections() {
      const value = Number(this.field?.maxSelections);
      return Number.isInteger(value) && value > 0 ? value : null;
    },
    canSelectMoreCheckboxOptions() {
      if (!this.maxSelections) return true;
      return this.localCheckboxValues.length < this.maxSelections;
    },
    normalizedFieldLink() {
      const raw = String(this.field?.link || "").trim();
      if (!raw) return "";
      if (/^https?:\/\//i.test(raw)) return raw;
      return `https://${raw}`;
    },
    hasFieldLink() {
      return !!this.normalizedFieldLink;
    }
  },
  methods: {
    getAllowedMimeTypesByFieldType(fieldType) {
      return getAllowedMimeTypesByFieldType(fieldType);
    },
    getAcceptByFieldType(fieldType) {
      return this.getAllowedMimeTypesByFieldType(fieldType).join(",");
    },
    normalizeFileRuleValue(value) {
      if (Array.isArray(value)) return value[0] || null;
      return value;
    },
    isAllowedMimeType(fieldType, mimeType) {
      return isAllowedMimeType(fieldType, mimeType);
    },
    syncFromProp(newValue) {
      if (this.field?.type === "radio") {
        this.localRadioValue =
          typeof newValue === "string" || typeof newValue === "number"
            ? String(newValue)
            : "";
        return;
      }

      if (
        this.field?.type === "checkbox" ||
        this.field?.type === "checkbox-image"
      ) {
        this.localCheckboxValues = Array.isArray(newValue) ? [...newValue] : [];
      }
    },
    getOptionValue(option) {
      const value = String(option?.value || "").trim();
      if (value) return value;
      return String(option?.label || "").trim();
    },
    getValidationRules(field) {
      const rules = [];
      const errorMsg = field.helpText || "Dieses Feld ist erforderlich";

      if (field.required) {
        rules.push(v => !!v || errorMsg);
      }

      if (field.validation === "email") {
        rules.push(
          v =>
            !v ||
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ||
            field.helpText ||
            "Gültige E-Mail-Adresse erforderlich"
        );
      }

      if (field.validation === "url") {
        rules.push(
          v =>
            !v ||
            /^https?:\/\/.+/.test(v) ||
            field.helpText ||
            "Gültige URL erforderlich"
        );
      }

      if (field.validation === "number") {
        rules.push(
          v => !v || !isNaN(v) || field.helpText || "Nur Zahlen erlaubt"
        );
      }

      if (field.validation === "phone") {
        rules.push(
          v =>
            !v ||
            /^[\d\s\-+()]+$/.test(v) ||
            field.helpText ||
            "Gültige Telefonnummer erforderlich"
        );
      }

      if (field.type === "file" || field.type === "image") {
        rules.push(v => {
          const fileValue = this.normalizeFileRuleValue(v);
          if (!fileValue || typeof fileValue !== "object") return true;
          if (fileValue.url) return true;
          if (!fileValue.type) return true;
          return (
            this.isAllowedMimeType(field.type, fileValue.type) ||
            "Dateityp ist nicht erlaubt"
          );
        });
      }

      return rules;
    },
    setCheckboxSelection(value, isSelected) {
      const newValue = [...this.localCheckboxValues];
      const index = newValue.indexOf(value);
      if (isSelected && index === -1) newValue.push(value);
      if (!isSelected && index > -1) newValue.splice(index, 1);
      this.localCheckboxValues = newValue;
      this.$emit("input", [...newValue]);
    },
    onCheckboxChange(value, checked) {
      if (typeof checked === "boolean") {
        this.setCheckboxSelection(value, checked);
        return;
      }
      // Fallback, falls Vuetify kein bool liefert
      this.toggleCheckboxValue(value);
    },
    toggleCheckboxValue(value) {
      const isSelected = this.isOptionSelected(value);
      if (!isSelected && !this.canSelectMoreCheckboxOptions) {
        return;
      }
      this.setCheckboxSelection(value, !isSelected);
    },
    isOptionSelected(value) {
      return this.localCheckboxValues.includes(value);
    },
    getOptionImageUrl(option) {
      if (!option?.image) return "";
      return option.image.url || option.image;
    }
  }
};
</script>

<style scoped lang="scss">
.form-field-renderer {
  width: 100%;
}

.form-field-renderer__image-options {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
}

.form-field-renderer__image-option {
  border: 1px solid #d7d7d7;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  transition: border-color 0.2s;
}

.form-field-renderer__image-option.selected {
  border-color: #97bd0f;
}

.form-field-renderer__image-option.is-disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.form-field-renderer__label-link {
  color: inherit;
  text-decoration: underline;
}
</style>
